import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useUnifiedUsers, UnifiedUser } from '@/hooks/useUnifiedUsers';
import { useAdminUsers } from '@/hooks/useAdminUsers';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UserCard } from '@/components/crm/UserCard';
import { AddUserModal } from '@/components/crm/AddUserModal';
import { UserFilters } from '@/components/crm/UserFilters';
import { ProjectAssignmentModal } from '@/components/crm/ProjectAssignmentModal';
import { Users, UserPlus, Shield, UserCog } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const CRMUserManagement = () => {
  const { hasRole, user } = useAuth();
  const isSuperAdmin = hasRole('super_admin');
  
  const { 
    users, 
    loading, 
    fetchAllUsers, 
    createChargingUser, 
    setUserTenant, 
    togglePortalUserActive 
  } = useUnifiedUsers();
  
  const {
    inviteUser,
    updateUserRole,
    resetUserPassword,
    deleteUser,
    impersonateUser,
  } = useAdminUsers();

  const [showAddModal, setShowAddModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('all');
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
  const [selectedUserForAssignment, setSelectedUserForAssignment] = useState<UnifiedUser | null>(null);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchAllUsers();
    }
  }, [isSuperAdmin]);

  if (!isSuperAdmin) {
    return (
      <div className="container mx-auto py-8">
        <Card className="p-6">
          <p className="text-muted-foreground">
            Je hebt geen toegang tot gebruikersbeheer.
          </p>
        </Card>
      </div>
    );
  }

  // Filter users
  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.display_name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesRole = roleFilter === 'all' || user.roles.includes(roleFilter);
    const matchesType = typeFilter === 'all' || user.user_type === typeFilter;
    
    const matchesTab = 
      activeTab === 'all' ||
      (activeTab === 'crm' && user.user_type === 'crm') ||
      (activeTab === 'portal' && user.user_type === 'portal') ||
      (activeTab === 'charging' && user.roles.includes('charging_user'));

    return matchesSearch && matchesRole && matchesType && matchesTab;
  });

  // Calculate stats
  const stats = {
    total: users.length,
    crm: users.filter(u => u.user_type === 'crm').length,
    portal: users.filter(u => u.user_type === 'portal').length,
    charging: users.filter(u => u.roles.includes('charging_user')).length,
    active: users.filter(u => u.is_active).length,
  };

  const handleAddCRMUser = async (email: string, role: 'admin' | 'super_admin' | 'crm_user' | 'charging_user') => {
    const success = await inviteUser(email, role as any);
    if (success) {
      await fetchAllUsers();
    }
    return success;
  };

  const handleAddPortalUser = async (email: string, role: string, linkedId: string) => {
    // This would need to be implemented in the edge function
    // For now, show a toast that this needs manual setup
    return false;
  };

  const handleAssignProjects = (userId: string) => {
    const foundUser = users.find(u => u.id === userId);
    if (foundUser) {
      setSelectedUserForAssignment(foundUser);
      setAssignmentModalOpen(true);
    }
  };

  const handleImpersonate = async (userId: string) => {
    const loginUrl = await impersonateUser(userId);
    if (loginUrl) {
      window.open(loginUrl, '_blank');
    }
  };

  return (
    <div className="container mx-auto py-4 sm:py-6 px-3 sm:px-6 space-y-4 sm:space-y-6 overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Gebruikersbeheer</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Beheer CRM, Portal en Charging gebruikers
          </p>
        </div>
        <Button onClick={() => setShowAddModal(true)} size="sm" className="w-full sm:w-auto sm:size-lg">
          <UserPlus className="h-4 w-4 mr-2" />
          <span className="sm:hidden">Toevoegen</span>
          <span className="hidden sm:inline">Gebruiker toevoegen</span>
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
        <Card className="p-3 sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-1.5 sm:p-2 bg-primary/10 rounded-lg shrink-0">
              <Users className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-xl sm:text-2xl font-bold">{stats.total}</p>
              <p className="text-xs text-muted-foreground truncate">Totaal</p>
            </div>
          </div>
        </Card>

        <Card className="p-3 sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-1.5 sm:p-2 bg-blue-500/10 rounded-lg shrink-0">
              <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500" />
            </div>
            <div className="min-w-0">
              <p className="text-xl sm:text-2xl font-bold">{stats.crm}</p>
              <p className="text-xs text-muted-foreground truncate">CRM</p>
            </div>
          </div>
        </Card>

        <Card className="p-3 sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-1.5 sm:p-2 bg-purple-500/10 rounded-lg shrink-0">
              <UserCog className="h-4 w-4 sm:h-5 sm:w-5 text-purple-500" />
            </div>
            <div className="min-w-0">
              <p className="text-xl sm:text-2xl font-bold">{stats.portal}</p>
              <p className="text-xs text-muted-foreground truncate">Portal</p>
            </div>
          </div>
        </Card>

        <Card className="p-3 sm:p-4 hidden sm:block">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-1.5 sm:p-2 bg-green-500/10 rounded-lg shrink-0">
              <Users className="h-4 w-4 sm:h-5 sm:w-5 text-green-500" />
            </div>
            <div className="min-w-0">
              <p className="text-xl sm:text-2xl font-bold">{stats.charging}</p>
              <p className="text-xs text-muted-foreground truncate">Charging</p>
            </div>
          </div>
        </Card>

        <Card className="p-3 sm:p-4 hidden lg:block">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-1.5 sm:p-2 bg-emerald-500/10 rounded-lg shrink-0">
              <Users className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-500" />
            </div>
            <div className="min-w-0">
              <p className="text-xl sm:text-2xl font-bold">{stats.active}</p>
              <p className="text-xs text-muted-foreground truncate">Actief</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-3 sm:p-6">
        <UserFilters
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          roleFilter={roleFilter}
          onRoleFilterChange={setRoleFilter}
          typeFilter={typeFilter}
          onTypeFilterChange={setTypeFilter}
        />
      </Card>

      {/* Tabs & User List */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">
            Alle ({users.length})
          </TabsTrigger>
          <TabsTrigger value="crm">
            CRM ({stats.crm})
          </TabsTrigger>
          <TabsTrigger value="portal">
            Portal ({stats.portal})
          </TabsTrigger>
          <TabsTrigger value="charging">
            Charging ({stats.charging})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {loading ? (
            <Card className="p-8">
              <p className="text-center text-muted-foreground">Laden...</p>
            </Card>
          ) : filteredUsers.length === 0 ? (
            <Card className="p-8">
              <p className="text-center text-muted-foreground">
                Geen gebruikers gevonden
              </p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredUsers.map(u => (
                <UserCard
                  key={u.id}
                  user={u}
                  currentUserId={user?.id}
                  onResetPassword={resetUserPassword}
                  onDelete={deleteUser}
                  onToggleActive={togglePortalUserActive}
                  onSetTenant={(userId) => {
                    // Show tenant selection dialog
                    console.log('Set tenant for:', userId);
                  }}
                  onAssignProjects={handleAssignProjects}
                  onImpersonate={handleImpersonate}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add User Modal */}
      <AddUserModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAddCRMUser={handleAddCRMUser}
        onAddPortalUser={handleAddPortalUser}
        onAddChargingUser={createChargingUser}
      />

      {/* Project Assignment Modal */}
      {selectedUserForAssignment && (
        <ProjectAssignmentModal
          open={assignmentModalOpen}
          onOpenChange={setAssignmentModalOpen}
          portalUser={{
            id: selectedUserForAssignment.id,
            email: selectedUserForAssignment.email,
            role: selectedUserForAssignment.roles[0]
          }}
        />
      )}
    </div>
  );
};

export default CRMUserManagement;
